/**
 * 설비별 표시 필드 (계획서 §13.2).
 *
 * 컴프레서 데모 가정 안내는 사용자 요청으로 제거하고 운전 수치만 표시한다.
 */

import type { EquipmentSnapshot } from "@/types/api";
import {
  dryerBadge,
  formatCount,
  formatDuration,
  formatTime,
  productionBadge,
  runningBadge,
  withUnit,
} from "@/lib/format";
import type { StatusBadge } from "@/lib/format";

interface Row {
  label: string;
  value: string;
}

function Card({
  title,
  badge,
  rows,
}: {
  title: string;
  badge: StatusBadge;
  rows: Row[];
}): React.JSX.Element {
  return (
    <section className={`equipment-card tone-${badge.tone}`}>
      <header>
        <h3>{title}</h3>
        <span className="badge">
          <span aria-hidden="true">{badge.icon}</span> {badge.label}
        </span>
      </header>
      <dl>
        {rows.map((row) => (
          <div key={row.label}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function EquipmentDetail({
  equipment,
}: {
  equipment: EquipmentSnapshot;
}): React.JSX.Element {
  const { welder, epoxy, dryer, dust_collector: collector, compressor } = equipment;

  return (
    <div className="equipment-grid">
      <Card
        title="용접기"
        badge={productionBadge(welder.state)}
        rows={[
          { label: "오늘 생산량", value: `${formatCount(welder.units_completed)}개` },
          { label: "cycle", value: withUnit(welder.cycle_seconds, "초/개") },
          { label: "WIP", value: `${formatCount(welder.wip)}개` },
          { label: "완료 ETA", value: formatTime(welder.finished_at) },
        ]}
      />

      <Card
        title="에폭시 주입기"
        badge={productionBadge(epoxy.state)}
        rows={[
          {
            label: "수리 경과",
            value:
              epoxy.repair_total_seconds === null
                ? "—"
                : `${formatDuration(epoxy.repair_elapsed_seconds)} / ${formatDuration(epoxy.repair_total_seconds)}`,
          },
          { label: "오늘 생산량", value: `${formatCount(epoxy.units_completed)}개` },
          { label: "cycle", value: withUnit(epoxy.cycle_seconds, "초/개") },
          { label: "완료 ETA", value: formatTime(epoxy.finished_at) },
        ]}
      />

      <Card
        title="건조기"
        badge={dryer ? dryerBadge(dryer.mode) : { icon: "■", label: "정지", tone: "idle" }}
        rows={
          dryer
            ? [
                { label: "상단 온도", value: withUnit(dryer.temperature_c, "℃") },
                { label: "heater duty", value: withUnit(dryer.heater_duty_pct, "%") },
                { label: "fan", value: dryer.fan_on ? "◎ 운전" : "○ 정지" },
                {
                  label: "안정화 경과",
                  value: `${formatDuration(dryer.stabilize_elapsed_seconds)} / ${formatDuration(dryer.stabilize_total_seconds)}`,
                },
                { label: "READY", value: formatTime(dryer.ready_at) },
                { label: "완료시각", value: formatTime(dryer.cooldown_completed_at) },
              ]
            : [{ label: "상태", value: "계획 수립 전" }]
        }
      />

      <Card
        title="집진기"
        badge={runningBadge(collector?.running ?? false)}
        rows={
          collector
            ? [
                { label: "Hz", value: withUnit(collector.hz, "Hz") },
                { label: "kW", value: withUnit(collector.power_kw, "kW") },
                { label: "건조기 연동", value: collector.follows_dryer_mode },
              ]
            : [{ label: "상태", value: "계획 수립 전" }]
        }
      />

      <Card
        title="컴프레서"
        badge={runningBadge(compressor.running)}
        rows={[
          { label: "목표 MPa", value: withUnit(compressor.target_mpa, "MPa") },
          { label: "Hz", value: withUnit(compressor.hz, "Hz") },
          { label: "tank", value: withUnit(compressor.tank_pct, "%") },
          { label: "kW", value: withUnit(compressor.power_kw, "kW") },
          {
            label: "수요 설비",
            value: compressor.consumers.length > 0 ? compressor.consumers.join(", ") : "없음",
          },
        ]}
      />
    </div>
  );
}
